import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { INPUT } from "@/components/pageSettings/AdminModal";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

function result(t) {
  if (t.status === "stopped") return "Stopped — nothing was switched";
  const best = t.standings.find((r) => r.key === t.winner);
  const how = t.decided_by === "automatic" ? "switched automatically" : `approved by ${t.decided_by}`;
  return `Winner: ${t.winner_name}${best?.tap_rate != null ? ` (${best.tap_rate}% tap rate)` : ""} — ${how}`;
}

function HistoryRow({ t, p, showWho }) {
  return (
    <details className="border-b border-zinc-200 py-2" data-testid={`${p}-history-${t.run_id}`}>
      <summary className="cursor-pointer text-sm">
        <span className="text-zinc-500">{fmt(t.started_at)} – {fmt(t.ended_at || t.winner_at)}</span>
        {showWho && <b className="ml-2">{t.kind === "buyer" ? t.firm_name || t.buyer : "Overall test"}</b>}
        <span className={`ml-2 ${t.status === "stopped" ? "text-zinc-500" : "text-emerald-700"}`}>{result(t)}</span>
      </summary>
      <table className="mt-2 w-full text-[12px]">
        <thead><tr className="text-left label-mono"><th className="pr-3">Design</th><th className="pr-3 text-right">Views</th><th className="pr-3 text-right">Taps</th><th className="text-right">Tap rate</th></tr></thead>
        <tbody>
          {t.standings.map((r) => (
            <tr key={r.key} className={r.key === t.winner ? "font-bold" : ""}>
              <td className="pr-3">{r.name}</td><td className="pr-3 text-right">{r.views}</td><td className="pr-3 text-right">{r.taps}</td>
              <td className="text-right">{r.tap_rate == null ? "—" : `${r.tap_rate}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-[11px] text-zinc-500">
        Winner after {t.min_views} views each{t.kind === "overall" ? ` · ${t.scope === "all" ? "every page" : "pages using the default design"}` : ""} · started by {t.started_by || "—"}
      </p>
    </details>
  );
}

// Past tests and their winners. admin: url = /admin/thankyou-test-history (all tests, with a filter).
export function ThankYouTestHistory({ url, version, admin = false, p = "ty-test" }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [buyers, setBuyers] = useState([]);
  useEffect(() => {
    api.get(url, { params: admin ? { page: filter } : undefined })
      .then((r) => {
        setRows(r.data.tests);
        if (admin && filter === "all") {
          setBuyers([...new Map(r.data.tests.filter((t) => t.kind === "buyer").map((t) => [t.page_id, t.firm_name || t.buyer])).entries()]);
        }
      })
      .catch(() => setRows([]));
  }, [url, version, filter, admin]);
  if (!rows) return null;
  return (
    <div className="space-y-2 border-t-2 border-black pt-3" data-testid={`${p}-history`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="font-chivo font-black">Past tests</div>
        {admin && (
          <select className={`${INPUT} mt-0 w-auto`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`${p}-history-filter`}>
            <option value="all">All tests</option>
            <option value="overall">Overall tests only</option>
            {buyers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
      </div>
      {!rows.length && <p className="text-[12px] text-zinc-500" data-testid={`${p}-history-empty`}>No finished tests yet. Each test shows here once it picks a winner or is stopped.</p>}
      {rows.map((t) => <HistoryRow key={t.run_id} t={t} p={p} showWho={admin} />)}
    </div>
  );
}
